import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Button,
  Space,
  Table,
  Typography,
  Row,
  Col,
  Statistic,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  message,
  Divider,
  Alert,
  Progress,
  Checkbox,
  Upload,
  Steps
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  BankOutlined,
  UploadOutlined,
  DownloadOutlined,
  ReconciliationOutlined,
  DollarOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface BankStatement {
  id: string;
  date: string;
  description: string;
  reference: string;
  debit: number;
  credit: number;
  balance: number;
  reconciled: boolean;
  matchedEntryId?: string;
}

interface ReconciliationSession {
  id: string;
  bankAccount: string;
  periodStart: string;
  periodEnd: string;
  openingBalance: number;
  closingBalance: number;
  statementBalance: number;
  status: 'in_progress' | 'completed' | 'discrepancy';
  reconciledCount: number;
  unreconciledCount: number;
}

const BankReconciliation: React.FC = () => {
  const [activeTab, setActiveTab] = useState('reconciliation');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [bankStatements, setBankStatements] = useState<BankStatement[]>([]);
  const [currentSession, setCurrentSession] = useState<ReconciliationSession | null>(null);
  const [form] = Form.useForm();

  const sampleBankStatements: BankStatement[] = [
    {
      id: '1',
      date: dayjs().subtract(2, 'days').format('YYYY-MM-DD'),
      description: 'Customer Payment - Acme Corp',
      reference: 'CHQ001',
      debit: 0,
      credit: 5000,
      balance: 25000,
      reconciled: true,
      matchedEntryId: 'JE-001'
    },
    {
      id: '2',
      date: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
      description: 'Office Rent Payment',
      reference: 'TXN002',
      debit: 2000,
      credit: 0,
      balance: 23000,
      reconciled: false
    },
    {
      id: '3',
      date: dayjs().format('YYYY-MM-DD'),
      description: 'Bank Charges',
      reference: 'CHG003',
      debit: 25,
      credit: 0,
      balance: 22975,
      reconciled: false
    }
  ];

  const sampleSession: ReconciliationSession = {
    id: '1',
    bankAccount: 'Main Bank Account',
    periodStart: dayjs().subtract(7, 'days').format('YYYY-MM-DD'),
    periodEnd: dayjs().format('YYYY-MM-DD'),
    openingBalance: 20000,
    closingBalance: 22975,
    statementBalance: 22975,
    status: 'in_progress',
    reconciledCount: 1,
    unreconciledCount: 2
  };

  useEffect(() => {
    setBankStatements(sampleBankStatements);
    setCurrentSession(sampleSession);
  }, []);

  const handleAutoReconcile = async () => {
    try {
      setLoading(true);
      // API call for auto reconciliation would go here
      message.success('Auto reconciliation completed');
    } catch (error) {
      message.error('Auto reconciliation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleManualMatch = async (statementId: string, entryId: string) => {
    try {
      // API call for manual matching would go here
      message.success('Transaction matched successfully');
    } catch (error) {
      message.error('Failed to match transaction');
    }
  };

  const statementColumns: ColumnsType<BankStatement> = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix()
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description'
    },
    {
      title: 'Reference',
      dataIndex: 'reference',
      key: 'reference'
    },
    {
      title: 'Debit',
      dataIndex: 'debit',
      key: 'debit',
      render: (amount) => amount > 0 ? `$${amount.toLocaleString()}` : '-'
    },
    {
      title: 'Credit',
      dataIndex: 'credit',
      key: 'credit',
      render: (amount) => amount > 0 ? `$${amount.toLocaleString()}` : '-'
    },
    {
      title: 'Balance',
      dataIndex: 'balance',
      key: 'balance',
      render: (amount) => `$${amount.toLocaleString()}`
    },
    {
      title: 'Status',
      dataIndex: 'reconciled',
      key: 'reconciled',
      render: (reconciled, record) => (
        <Tag color={reconciled ? 'green' : 'orange'}>
          {reconciled ? 'RECONCILED' : 'UNMATCHED'}
        </Tag>
      )
    },
    {
      title: 'Matched Entry',
      dataIndex: 'matchedEntryId',
      key: 'matchedEntryId',
      render: (entryId) => entryId ? (
        <Button type="link" size="small">{entryId}</Button>
      ) : '-'
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {!record.reconciled && (
            <Button 
              type="text" 
              size="small"
              onClick={() => {/* Open match modal */}}
            >
              Match
            </Button>
          )}
          <Button type="text" icon={<EyeOutlined />} size="small" />
        </Space>
      )
    }
  ];

  const renderReconciliationTab = () => (
    <div>
      {currentSession && (
        <Card style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <Statistic
                title="Opening Balance"
                value={currentSession.openingBalance}
                prefix="$"
                precision={2}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Closing Balance"
                value={currentSession.closingBalance}
                prefix="$"
                precision={2}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Statement Balance"
                value={currentSession.statementBalance}
                prefix="$"
                precision={2}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Difference"
                value={Math.abs(currentSession.closingBalance - currentSession.statementBalance)}
                prefix="$"
                precision={2}
                valueStyle={{ 
                  color: currentSession.closingBalance === currentSession.statementBalance ? '#3f8600' : '#cf1322' 
                }}
              />
            </Col>
          </Row>
          
          <Divider />
          
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Progress
                type="circle"
                percent={(currentSession.reconciledCount / (currentSession.reconciledCount + currentSession.unreconciledCount)) * 100}
                format={() => `${currentSession.reconciledCount}/${currentSession.reconciledCount + currentSession.unreconciledCount}`}
              />
              <div style={{ textAlign: 'center', marginTop: 8 }}>
                <Text>Reconciled Transactions</Text>
              </div>
            </Col>
            <Col span={12}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button 
                  type="primary" 
                  icon={<ReconciliationOutlined />}
                  onClick={handleAutoReconcile}
                  loading={loading}
                  block
                >
                  Auto Reconcile
                </Button>
                <Button icon={<UploadOutlined />} block>
                  Import Statement
                </Button>
                <Button icon={<DownloadOutlined />} block>
                  Export Report
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>
      )}

      <Card
        title="Bank Statement Transactions"
        extra={
          <Space>
            <Button type="primary" icon={<PlusOutlined />}>
              Add Transaction
            </Button>
            <Button icon={<UploadOutlined />}>
              Import CSV
            </Button>
          </Space>
        }
      >
        <Table
          columns={statementColumns}
          dataSource={bankStatements}
          rowKey="id"
          loading={loading}
          rowSelection={{
            selectedRowKeys: selectedRows,
            onChange: setSelectedRows,
            getCheckboxProps: (record) => ({
              disabled: record.reconciled
            })
          }}
          summary={(pageData) => {
            const totalDebit = pageData.reduce((sum, record) => sum + record.debit, 0);
            const totalCredit = pageData.reduce((sum, record) => sum + record.credit, 0);
            return (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={3}>
                  <strong>Total</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <strong>${totalDebit.toLocaleString()}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2}>
                  <strong>${totalCredit.toLocaleString()}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} colSpan={4}>
                  <strong>Net: ${(totalCredit - totalDebit).toLocaleString()}</strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            );
          }}
        />
      </Card>
    </div>
  );

  const renderHistoryTab = () => (
    <Card title="Reconciliation History">
      <Table
        columns={[
          { title: 'Period', dataIndex: 'period', key: 'period' },
          { title: 'Bank Account', dataIndex: 'bankAccount', key: 'bankAccount' },
          { title: 'Status', dataIndex: 'status', key: 'status' },
          { title: 'Reconciled Date', dataIndex: 'reconciledDate', key: 'reconciledDate' },
          { title: 'Actions', key: 'actions', render: () => <Button type="link">View</Button> }
        ]}
        dataSource={[]}
        rowKey="id"
      />
    </Card>
  );

  const renderSettingsTab = () => (
    <Card title="Reconciliation Settings">
      <Form layout="vertical">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Auto-match Threshold">
              <InputNumber
                placeholder="Amount threshold for auto-matching"
                style={{ width: '100%' }}
                formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Date Tolerance (days)">
              <InputNumber
                placeholder="Date tolerance for matching"
                style={{ width: '100%' }}
                min={0}
                max={30}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item>
              <Checkbox>Enable automatic reconciliation</Checkbox>
            </Form.Item>
            <Form.Item>
              <Checkbox>Send reconciliation notifications</Checkbox>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Card>
  );

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>Bank Reconciliation</Title>
        <Text type="secondary">
          Reconcile bank statements with accounting records
        </Text>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <Tabs.TabPane tab="Reconciliation" key="reconciliation">
          {renderReconciliationTab()}
        </Tabs.TabPane>
        <Tabs.TabPane tab="History" key="history">
          {renderHistoryTab()}
        </Tabs.TabPane>
        <Tabs.TabPane tab="Settings" key="settings">
          {renderSettingsTab()}
        </Tabs.TabPane>
      </Tabs>
    </div>
  );
};

export default BankReconciliation;
